import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { type State, store } from "@App/state";
import { setFleetVehicleReturn, type FleetVehicleReturn } from "@App/state/reducers/fleet";
import { z } from "zod";

const vehicleReturnSchema = z.object({
  agreementNumber: z.string(),
  endDate: z.string(),
  authorizedReturnLocationIds: z.array(z.string()),
});

function decodeVehicleReturn(encoded: string): FleetVehicleReturn | null {
  try {
    const json = JSON.parse(Buffer.from(encoded, "base64").toString("utf-8"));
    const result = vehicleReturnSchema.safeParse(json);
    if (!result.success) return null;
    return result.data;
  } catch {
    return null;
  }
}

function encodeVehicleReturn(vr: FleetVehicleReturn): string {
  return Buffer.from(JSON.stringify(vr), "utf-8").toString("base64");
}

export const useFleetVehicleReturnParameters = () => {
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const existing = useSelector((state: State) => state.fleet.vehicleReturn);

  const vehicleReturnParam = searchParams.get("vehicleReturn");

  useEffect(() => {
    if (!vehicleReturnParam || existing) return;
    const decoded = decodeVehicleReturn(vehicleReturnParam);
    if (decoded) {
      dispatch(setFleetVehicleReturn(decoded));
    }
  }, [vehicleReturnParam]);

  const fleetVehicleReturnQueryParams = (): Record<string, string> => {
    const vr = store.getState().fleet.vehicleReturn;
    if (!vr) return {};
    return { vehicleReturn: encodeVehicleReturn(vr) };
  };

  return { fleetVehicleReturnQueryParams };
};
